import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  AlertTriangle,
  FileText,
  Phone,
  ShieldCheck,
  ClipboardList,
  DollarSign,
  Award,
  Ban,
  CheckCircle2,
  Package,
} from "lucide-react";
import Header from "./site/Header";
import Footer from "./site/Footer";
import Contact from "./site/Contact";
import { SITE } from "../site.config";
import useDocumentMeta from "../hooks/useDocumentMeta";

const SITE_URL = "https://powerzautoelectrical.com.au";
const ABN = "49 680 967 088";

const WORKSHOP_POLICIES = [
  {
    icon: AlertTriangle,
    tone: "warning",
    title: "Caravan & Camper Van Policy — Vehicles Only",
    bullets: [
      "Powerz Auto Electrical is an auto electrical workshop. We work on Vehicles ONLY — 4WDs, utes, sedans, trucks and machinery. We do NOT work on caravans, camper vans, or any wiring inside a caravan or camper van.",
      "Our work stops at the trailer plug or Anderson plug on the tow vehicle. Any caravan-side or camper-van-side wiring — including repairs, fault-finding, charging-system work, fridge wiring, water-pump wiring, interior lighting, solar, inverters or 240V — must be carried out by a dedicated caravan / camper-van electrical specialist.",
      "Powerz Auto Electrical accepts NO responsibility for the condition of the caravan or camper van, its internal wiring, charging system, fuses, appliances, solar regulator, batteries, inverter, or any pre-existing faults discovered during or after a vehicle-side install.",
    ],
  },
  {
    icon: FileText,
    tone: "warning",
    title: "Caravan Wiring Diagram Required",
    bullets: [
      "All 12 Pin trailer plug installs require the customer to supply a current wiring diagram for the caravan, camper trailer or trailer being towed. No diagram, no booking.",
      "All Red Anderson plug installs that connect to a caravan or camper trailer require the customer to supply a current wiring diagram. No diagram, no booking.",
      "This protects both you and us from miswiring a fridge, brake feed, charge line or 12V auxiliary into the wrong pin. Without a diagram we cannot guarantee compatible operation between the tow vehicle and the caravan.",
    ],
  },
];

const LEGAL_TERMS = [
  {
    icon: ShieldCheck,
    n: "1",
    title: "Limitation of Liability (Pre-Existing Conditions)",
    paragraphs: [
      "Powerz Auto Electrical accepts no liability for any faults, failures, or damage that existed prior to any inspection, installation, repair, or other work carried out on the vehicle.",
      "Any pre-existing conditions, including but not limited to electrical faults, wiring damage, corrosion, water ingress, or previously installed components (whether owner-supplied or fitted by third parties), remain the sole responsibility of the vehicle owner.",
      "Powerz Auto Electrical is not responsible for any subsequent failure or damage that may arise as a result of these pre-existing conditions, whether identified during service or not.",
    ],
  },
  {
    icon: ClipboardList,
    n: "2",
    title: "Scope of Work & Authorization",
    paragraphs: [
      "All work carried out by Powerz Auto Electrical is limited strictly to the services and repairs requested and authorized by the customer, and as outlined in the original invoice or job card.",
      "Powerz Auto Electrical will not carry out additional work outside of the approved scope without prior customer authorization. If further faults or required repairs are identified, the customer will be contacted to approve additional work and associated costs before proceeding.",
    ],
  },
  {
    icon: DollarSign,
    n: "3",
    title: "Pricing & Minimum Charge",
    paragraphs: [
      "Powerz Auto Electrical enforces a minimum charge of one (1) hour labour on all jobs.",
      "All pricing is set in accordance with company rates and is non-negotiable. By authorizing work, the customer agrees to the pricing structure and charges as outlined by Powerz Auto Electrical.",
    ],
  },
  {
    icon: Award,
    n: "4",
    title: "Workmanship Warranty",
    paragraphs: [
      "Powerz Auto Electrical provides a 12-month (1 year) workmanship warranty on services performed. This warranty applies solely to the quality of workmanship and installation carried out by Powerz Auto Electrical.",
      "This warranty does not cover:",
    ],
    list: [
      "Customer-supplied parts (see Section 5 — workmanship on the install is still warranted)",
      "Pre-existing faults or conditions",
      "Normal wear and tear",
      "Failures of components not supplied or installed by Powerz Auto Electrical",
    ],
  },
  {
    icon: Package,
    n: "5",
    title: "Customer-Supplied Parts",
    paragraphs: [
      "Powerz Auto Electrical may, at its sole discretion and on a case-by-case basis, agree to install parts or accessories supplied by the customer. Acceptance of a customer-supplied part is not guaranteed and must be confirmed before booking.",
      "Where a customer-supplied part is fitted, the following conditions apply:",
    ],
    list: [
      "Powerz Auto Electrical provides no warranty, guarantee or representation of any kind on the customer-supplied part itself — including its quality, suitability, compatibility with the vehicle, compliance with Australian Design Rules or any other standard, or its fitness for purpose.",
      "The 12-month workmanship warranty applies only to the installation workmanship carried out by Powerz Auto Electrical, not to the part.",
      "If a customer-supplied part is faulty, dead on arrival, incomplete, incorrect or incompatible, all labour time spent diagnosing, removing, re-fitting or working around that part is chargeable at the standard workshop rate, in addition to the original quoted install.",
      "Powerz Auto Electrical accepts no liability for any damage, fault or loss to the vehicle, its electrical systems or other components caused by or arising from a customer-supplied part, whether at the time of installation or afterwards.",
      "All warranty claims, returns or refunds for a customer-supplied part are between the customer and their supplier. Powerz Auto Electrical will not pursue, process or facilitate such claims.",
      "Powerz Auto Electrical reserves the right to refuse to install any customer-supplied part that it considers unsafe, non-compliant, of unacceptable quality or unsuitable for the vehicle, including after the part has been inspected at the workshop. Where a part is refused, the minimum one (1) hour labour charge may apply for time already spent.",
      "Customer-supplied parts must be delivered complete, with all hardware, harnesses, fuses and manufacturer fitting instructions. Missing items will be charged as additional time and/or parts.",
    ],
  },
  {
    icon: Ban,
    n: "6",
    title: "Warranty Void Conditions",
    paragraphs: ["The workmanship warranty will be deemed void immediately if:"],
    list: [
      "Any work carried out by Powerz Auto Electrical is altered, modified, or tampered with",
      "The vehicle or repaired components are worked on by a third party without prior written approval from Powerz Auto Electrical",
      "Damage occurs due to misuse, neglect, or external factors beyond Powerz Auto Electrical's control",
    ],
  },
  {
    icon: CheckCircle2,
    n: "7",
    title: "Customer Acknowledgement",
    paragraphs: [
      "By proceeding with any work, the customer acknowledges and agrees to the terms outlined in this document and accepts that Powerz Auto Electrical is not liable for any pre-existing faults or consequential damage arising from them.",
    ],
  },
];

export default function PoliciesPage() {
  const canonical = `${SITE_URL}/policies`;

  useDocumentMeta({
    title: "Workshop Policies, Liability Disclaimer & Warranty | Powerz Auto Electrical",
    description:
      "Workshop policies, liability disclaimer and 12-month workmanship warranty for Powerz Auto Electrical (ABN 49 680 967 088). Includes caravan policy, caravan wiring-diagram requirement, scope of work, minimum charge, and warranty void conditions.",
    canonical,
    image: SITE.business.logo,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Workshop Policies", item: canonical },
        ],
      },
    ],
  });

  return (
    <div className="bg-black text-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-black pt-40 md:pt-48 pb-12 md:pb-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-pz transition-colors bebas tracking-widest text-sm mb-10"
            data-testid="policies-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            HOME
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base">
              POWERZ AUTO ELECTRICAL
            </span>
          </div>

          <h1
            data-testid="policies-headline"
            className="bebas text-white text-5xl md:text-7xl leading-[0.9] tracking-tight max-w-4xl"
          >
            WORKSHOP POLICIES &amp;{" "}
            <span className="text-cyan-pz glow-cyan">LIABILITY DISCLAIMER.</span>
          </h1>

          <p className="mt-6 text-white/70 barlow text-base md:text-lg max-w-3xl">
            The terms we work to, in plain English. By authorising any work at our
            Kunda Park workshop you agree to the disclaimer and workmanship
            warranty terms below. If anything here is unclear, give us a bell on{" "}
            <a
              href={`tel:${SITE.business.phoneTel}`}
              className="text-cyan-pz hover:underline"
            >
              {SITE.business.phone}
            </a>{" "}
            before booking in.
          </p>

          <div
            data-testid="policies-business-card"
            className="mt-8 inline-flex flex-wrap items-center gap-x-6 gap-y-2 border border-white/15 bg-[#0A0A0A] px-5 py-3 clip-tag"
          >
            <div>
              <span className="bebas text-white/40 tracking-widest text-xs">
                BUSINESS{" "}
              </span>
              <span className="text-white barlow text-sm">
                Powerz Auto Electrical
              </span>
            </div>
            <div>
              <span className="bebas text-white/40 tracking-widest text-xs">
                ABN{" "}
              </span>
              <span className="text-cyan-pz barlow text-sm tracking-wider">
                {ABN}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Workshop policies — warnings */}
      <section className="relative bg-black pb-12 md:pb-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 space-y-6">
          {WORKSHOP_POLICIES.map((p, idx) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`policy-${idx}`}
                className="border border-amber-400/40 bg-amber-400/5 p-6 md:p-10 clip-perf"
              >
                <div className="flex items-center gap-3 mb-5">
                  <Icon className="w-7 h-7 text-amber-400 shrink-0" />
                  <h2 className="bebas text-amber-400 text-2xl md:text-3xl tracking-[0.15em]">
                    {p.title.toUpperCase()}
                  </h2>
                </div>
                <ul className="space-y-3">
                  {p.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-white/85 barlow leading-relaxed"
                    >
                      <span className="bebas text-amber-400/80 tracking-widest text-sm mt-0.5 shrink-0">
                        0{i + 1}
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Legal — Liability Disclaimer & Workmanship Warranty */}
      <section
        data-testid="policies-legal-section"
        className="relative bg-black pb-24 md:pb-32"
      >
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base">
              LIABILITY DISCLAIMER &amp; WORKMANSHIP WARRANTY
            </span>
          </div>
          <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.95] mb-3">
            POWERZ AUTO ELECTRICAL — TERMS OF SERVICE
          </h2>
          <p className="text-white/50 barlow text-sm mb-12">
            ABN: <span className="text-cyan-pz">{ABN}</span>
          </p>

          <div className="space-y-5">
            {LEGAL_TERMS.map((s, idx) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.title}
                  data-testid={`legal-section-${idx}`}
                  className="border border-white/10 bg-[#0A0A0A] p-6 md:p-10 clip-perf"
                >
                  <div className="flex items-start gap-5">
                    <div className="hidden md:flex items-center justify-center w-14 h-14 border border-cyan-pz/40 bg-cyan-pz/5 shrink-0">
                      <Icon className="w-6 h-6 text-cyan-pz" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-4">
                        <Icon className="w-5 h-5 text-cyan-pz md:hidden" />
                        <span className="bebas text-cyan-pz/80 tracking-widest text-sm">
                          SECTION {s.n}
                        </span>
                      </div>
                      <h3 className="bebas text-white text-2xl md:text-3xl tracking-tight mb-5">
                        {s.title}
                      </h3>
                      {s.paragraphs.map((p, i) => (
                        <p
                          key={i}
                          className="text-white/85 barlow leading-relaxed mb-3 last:mb-0"
                        >
                          {p}
                        </p>
                      ))}
                      {s.list && (
                        <ul className="mt-3 space-y-2">
                          {s.list.map((li, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-3 text-white/80 barlow leading-relaxed"
                            >
                              <span className="text-cyan-pz mt-1.5 shrink-0">▸</span>
                              <span>{li}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <p className="mt-10 text-white/40 barlow text-xs italic">
            Last updated: May 2026 · This document forms part of the Terms of
            Service for any work carried out by Powerz Auto Electrical
            (ABN&nbsp;{ABN}) at our Kunda Park workshop.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={`tel:${SITE.business.phoneTel}`}
              data-testid="policies-call-btn"
              className="inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-7 py-4 bebas tracking-widest hover:bg-white transition-colors clip-perf"
            >
              <Phone className="w-4 h-4" />
              CALL {SITE.business.phone}
            </a>
            <Link
              to="/"
              data-testid="policies-home-link"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-7 py-4 bebas tracking-widest hover:border-cyan-pz hover:text-cyan-pz transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              BACK TO HOME
            </Link>
          </div>
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
}
